'use client';

import { useState } from 'react';
import { asText, asHTML, isFilled } from '@prismicio/client';
import type { RichTextField, LinkField } from '@prismicio/client';
import { sendLeadGenEmail } from '@/app/actions/send-leadgen-email';
import { getSubmissionContext } from '@/utils/attribution';
import { motion, AnimatePresence } from 'framer-motion';

interface LeadGenBenefit {
  benefit: RichTextField;
}

interface LeadGenSectionProps {
  copy: RichTextField;
  leadGenFile: LinkField;
  benefits?: LeadGenBenefit[];
  buttonText?: string;
  successMessage?: RichTextField;
  blogPostTitle?: string;
  adminEmails?: string[];
  fileTitle?: string;
}

export default function LeadGenSection({
  copy,
  leadGenFile,
  benefits,
  buttonText,
  successMessage,
  blogPostTitle,
  adminEmails,
  fileTitle,
}: LeadGenSectionProps) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('downloadUrl', isFilled.link(leadGenFile) ? leadGenFile.url || '' : '');
      formData.append('blogPostTitle', blogPostTitle || 'Unknown Blog Post');
      formData.append('adminEmails', JSON.stringify(adminEmails || []));
      formData.append('fileTitle', fileTitle || 'Your Guide');

      // Where the form was submitted from + how the visitor found us.
      Object.entries(getSubmissionContext()).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });

      const result = await sendLeadGenEmail(formData);

      if (result.success) {
        setIsSubmitted(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="my-8 mb-12 rounded-lg border border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100 p-6 shadow-sm">
      <AnimatePresence mode="wait">
        {!isSubmitted ? (
          <motion.div
            key="form"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mb-4">
              <div
                className="text-base font-semibold text-gray-800 sm:text-lg sm:font-normal"
                dangerouslySetInnerHTML={{ __html: asHTML(copy) || '' }}
              />

              {/* Benefits List */}
              {benefits && benefits.length > 0 && (
                <ul className="mb-8 mt-3 space-y-2">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <svg
                        className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-blue-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-gray-500">{asText(benefit.benefit)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex-1">
                <label htmlFor="leadgen-email" className="sr-only">
                  Email Address
                </label>
                <input
                  id="leadgen-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  required
                  disabled={isSubmitting}
                  className="w-full rounded-md border border-gray-300 px-4 py-2 text-center text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100 sm:text-left"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !email.trim()}
                className="inline-flex items-center justify-center rounded-md bg-blue-600 px-6 py-2 text-center font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-400 sm:justify-start sm:text-left"
              >
                {!isSubmitting && (
                  <svg
                    className="mr-2 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                )}
                {isSubmitting ? 'Sending...' : buttonText || 'Get Download Link'}
              </button>
            </form>

            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          </motion.div>
        ) : (
          <motion.div
            key="success"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="text-center"
          >
            <div className="mb-4">
              <motion.svg
                className="mx-auto h-12 w-12 text-blue-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </motion.svg>
            </div>
            <div className="text-gray-700">
              {successMessage ? (
                <div dangerouslySetInnerHTML={{ __html: asHTML(successMessage) || '' }} />
              ) : (
                <>
                  <h3 className="mb-2 text-lg font-medium text-gray-900">Thank you!</h3>
                  <p className="text-gray-600">
                    We&apos;ve sent you an email with the download link.
                  </p>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
